/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 27th 2022, 11:56:07 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import { FieldDependency, FieldDependencyCondition, FieldDependencyConditionGroup } from "lib/schemas";
import { getDefaultFieldDependencyCondition, getDefaultFieldDependencyConditionGroup } from "lib/factories";
import { ConditionSettings } from "common/components/conditions";
import * as formBuilderUtils from "form-builder/utils";
import { DecisionMakingIcon } from "theme/icons";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { OSDeleteButton, OSAlert } from "ui-widgets";
import { FieldDependencyLogicTypeSelector } from "./FieldDependencyLogicTypeSelector";

type FieldDependencyFormConditionsProps = { fieldDependency: FieldDependency };

export const FieldDependencyFormConditions = (props: FieldDependencyFormConditionsProps) => {
  const {
    fieldDependencyConditionsForRendering,
    errors,
    onChangeDependencyCondition,
    addNewDependencyCondition,
    removeDependencyCondition,
    addNewDependencyConditionGroup,
    onChangeDependencyConditionGroupLogicType,
  } = useFieldDependencyFormConditions(props);

  return (
    <>
      <Stack direction="row" spacing={2} className="title">
        <DecisionMakingIcon width={30} height={30} /> <Typography variant="h5">Conditions</Typography>
      </Stack>
      <Stack spacing={2}>
        {errors?.length ? (
          <OSAlert severity="error" variant="filled">
            {errors.map((error, index) => (
              <React.Fragment key={`${index}`}>
                {Object.keys(error).map((error_key) => (
                  <p key={error_key}>{error?.[error_key]["message"]}</p>
                ))}
              </React.Fragment>
            ))}
          </OSAlert>
        ) : null}

        {fieldDependencyConditionsForRendering.map((itemToRender) => {
          if (itemToRender?._isConditionGroup === false) {
            const condition = itemToRender.fieldDependencyCondition;
            return (
              <Stack direction="row" key={condition?.artifact_guid} className="single-condition">
                <FieldDependencyFormCondition
                  fieldDependencyCondition={condition}
                  onChangeDependencyCondition={onChangeDependencyCondition}
                />
                <OSDeleteButton onClick={() => removeDependencyCondition(condition.artifact_guid)} />
              </Stack>
            );
          }

          const fieldDependencyConditionGroup = itemToRender?.fieldDependencyConditionGroup;
          const groupedConditions = itemToRender?.groupedConditions;

          return (
            <>
              <Stack
                direction="column"
                spacing={2}
                key={fieldDependencyConditionGroup?.artifact_guid}
                className="condition-group"
              >
                <FieldDependencyLogicTypeSelector
                  conditionLogicType={fieldDependencyConditionGroup.condition_logic_type}
                  onChangeConditionLogicType={(new_logic_type) =>
                    onChangeDependencyConditionGroupLogicType(
                      fieldDependencyConditionGroup.artifact_guid,
                      new_logic_type
                    )
                  }
                />
                {groupedConditions.map((fieldDependencyCondition) => (
                  <Stack direction="row" key={fieldDependencyCondition?.artifact_guid}>
                    <FieldDependencyFormCondition
                      fieldDependencyCondition={fieldDependencyCondition}
                      onChangeDependencyCondition={onChangeDependencyCondition}
                    />
                    <OSDeleteButton onClick={() => removeDependencyCondition(fieldDependencyCondition.artifact_guid)} />
                  </Stack>
                ))}
                <Stack direction="row" className="add-button">
                  <Tooltip title={<Typography>Add New Condition To This Group</Typography>} arrow placement="top">
                    <div>
                      <IconButton
                        color="primary"
                        onClick={(event) => addNewDependencyCondition(fieldDependencyConditionGroup.id)}
                        sx={{ mt: 1 }}
                      >
                        <AddBoxIcon fontSize="large" />
                      </IconButton>
                    </div>
                  </Tooltip>
                </Stack>
              </Stack>
            </>
          );
        })}
        <Stack direction="row" className="add-button">
          <Tooltip title={<Typography>Add New Condition</Typography>} arrow placement="top">
            <div>
              <IconButton color="primary" onClick={(event) => addNewDependencyCondition(null)} sx={{ mt: 1 }}>
                <AddBoxIcon fontSize="large" />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title={<Typography>Add New Condition Group</Typography>} arrow placement="top">
            <div>
              <IconButton color="primary" onClick={addNewDependencyConditionGroup} sx={{ mt: 1 }}>
                <AddToPhotosIcon fontSize="large" />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      </Stack>
    </>
  );
};

type OnChangeDependencyCondition = (
  fieldDependencyConditionGUID: FieldDependencyCondition["artifact_guid"],
  data: FieldDependencyCondition
) => void;

const useFieldDependencyFormConditions = ({ fieldDependency }: FieldDependencyFormConditionsProps) => {
  const {
    useFormReturn: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useOSAPIFormContext<FieldDependency>();
  const fieldDependencyConditions = watch("field_dependency_conditions");
  const fieldDependencyConditionGroups = watch("field_dependency_condition_groups") || [];

  const addNewDependencyCondition = (groupId) => {
    setValue(
      "field_dependency_conditions",
      [...fieldDependencyConditions, getDefaultFieldDependencyCondition({ fieldDependency, groupId })],
      { shouldDirty: true }
    );
  };

  const addNewDependencyConditionGroup = () => {
    const newGroup = getDefaultFieldDependencyConditionGroup({ fieldDependency });
    setValue("field_dependency_condition_groups", [...fieldDependencyConditionGroups, newGroup], { shouldDirty: true });
    addNewDependencyCondition(newGroup.id);
  };

  const removeDependencyCondition = (fieldDependencyConditionGUID: FieldDependencyCondition["artifact_guid"]) => {
    setValue(
      "field_dependency_conditions",
      fieldDependencyConditions.filter(
        (fieldDependencyCondition) => fieldDependencyCondition.artifact_guid !== fieldDependencyConditionGUID
      ),
      { shouldDirty: true }
    );
  };

  const onChangeDependencyCondition: OnChangeDependencyCondition = (
    fieldDependencyConditionGUID: FieldDependencyCondition["artifact_guid"],
    data: FieldDependencyCondition
  ) => {
    const newFieldDependencyConditions = fieldDependencyConditions.map((dependencyCondition) => {
      if (dependencyCondition.artifact_guid === fieldDependencyConditionGUID) {
        return data;
      }
      return dependencyCondition;
    });
    setValue("field_dependency_conditions", newFieldDependencyConditions, { shouldDirty: true });
  };

  const onChangeDependencyConditionGroupLogicType = (
    fieldDependencyConditionGroupGUID: FieldDependencyConditionGroup["artifact_guid"],
    condition_logic_type: FieldDependencyConditionGroup["condition_logic_type"]
  ) => {
    const newFieldDependencyConditionGroups = fieldDependencyConditionGroups.map((dependencyConditionGroup) => {
      if (dependencyConditionGroup.artifact_guid === fieldDependencyConditionGroupGUID) {
        return { ...dependencyConditionGroup, condition_logic_type };
      }
      return dependencyConditionGroup;
    });
    setValue("field_dependency_condition_groups", newFieldDependencyConditionGroups, { shouldDirty: true });
  };

  return {
    fieldDependencyConditionsForRendering: formBuilderUtils.getFieldDependencyConditionsForRendering(
      fieldDependencyConditionGroups,
      fieldDependencyConditions
    ),
    errors: errors?.["field_dependency_conditions"],
    onChangeDependencyCondition,
    addNewDependencyCondition,
    removeDependencyCondition,
    addNewDependencyConditionGroup,
    onChangeDependencyConditionGroupLogicType,
  };
};

type FieldDependencyFormConditionProps = {
  fieldDependencyCondition: FieldDependencyCondition;
  onChangeDependencyCondition: OnChangeDependencyCondition;
};

const FieldDependencyFormCondition = (props: FieldDependencyFormConditionProps) => {
  const { onChangeConditionForm, onChangeConditionField, onChangeConditionOperator, onChangeConditionValue } =
    useFieldDependencyFormCondition(props);
  const { fieldDependencyCondition } = props;

  return (
    <ConditionSettings
      showFormPicker={false}
      conditionSettings={fieldDependencyCondition}
      onChangeConditionForm={onChangeConditionForm}
      onChangeConditionField={onChangeConditionField}
      onChangeConditionOperator={onChangeConditionOperator}
      onChangeConditionValue={onChangeConditionValue}
    />
  );
};

const useFieldDependencyFormCondition = ({
  fieldDependencyCondition,
  onChangeDependencyCondition,
}: FieldDependencyFormConditionProps) => {
  return {
    onChangeConditionForm: () => {},
    onChangeConditionField: (field) =>
      onChangeDependencyCondition(fieldDependencyCondition.artifact_guid, {
        ...fieldDependencyCondition,
        condition_field: field?.id,
      }),
    onChangeConditionOperator: (condition_operator) =>
      onChangeDependencyCondition(fieldDependencyCondition.artifact_guid, {
        ...fieldDependencyCondition,
        condition_operator,
      }),
    onChangeConditionValue: (condition_value) =>
      onChangeDependencyCondition(fieldDependencyCondition.artifact_guid, {
        ...fieldDependencyCondition,
        condition_value,
      }),
  };
};
